import React, { useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';

interface InfiniteScrollContainerProps {
  children: React.ReactNode;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  isLoading?: boolean;
  error?: Error | null;
  threshold?: number;
  rootMargin?: string;
  loadingComponent?: React.ReactNode;
  endMessage?: React.ReactNode;
  errorComponent?: React.ReactNode;
}

const InfiniteScrollContainer: React.FC<InfiniteScrollContainerProps> = ({
  children,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  isLoading = false,
  error = null,
  threshold = 0.1,
  rootMargin = '100px',
  loadingComponent,
  endMessage,
  errorComponent
}) => {
  const loadingRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      
      if (
        entry.isIntersecting && 
        hasNextPage && 
        !isFetchingNextPage && 
        !isLoading && 
        !error
      ) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, isLoading, error, fetchNextPage]
  );

  useEffect(() => {
    const loadingElement = loadingRef.current;
    
    if (!loadingElement) return;

    // Disconnect existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(handleIntersection, {
      threshold,
      rootMargin
    });

    // Start observing
    observerRef.current.observe(loadingElement);

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleIntersection, threshold, rootMargin]);

  // Default loading component
  const defaultLoadingComponent = (
    <div className="flex items-center justify-center py-8"
    >
      <div className="flex items-center gap-3">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
        <span className="text-sm text-slate-600 dark:text-slate-400">Loading more posts...</span>
      </div>
    </div>
  );

  // Default end message
  const defaultEndMessage = null;

  // Default error component
  const defaultErrorComponent = (
    <div className="flex items-center justify-center py-8"
    >
      <div className="text-center">
        <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-3">
          <span className="text-2xl">⚠️</span>
        </div>
        <p className="text-sm text-red-600 dark:text-red-400 font-medium mb-2">
          Failed to load more posts
        </p>
        <button
          onClick={fetchNextPage}
          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
        >
          Try again
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Main content */}
      {children}

      {/* Loading/End/Error indicator */}
      <div ref={loadingRef}>
        {error ? (
          errorComponent || defaultErrorComponent
        ) : isFetchingNextPage ? (
          loadingComponent || defaultLoadingComponent
        ) : !hasNextPage ? (
          endMessage || defaultEndMessage
        ) : (
          // Invisible trigger element
          <div className="h-1" />
        )}
      </div>
    </div>
  );
};

export default InfiniteScrollContainer; 